import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import mesApi, { FlourQualityQueueItem } from "../../../Api/mes.api";
import FlourQualityShell from "./components/FlourQualityShell";
import { deviceSummaryBadge, formatHrDate } from "./flourQualityUtils";

type QueueTab = "flour" | "other";

const FlourQualityQueue = () => {
  const [queue, setQueue] = useState<FlourQualityQueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [approvingLotId, setApprovingLotId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<QueueTab>("flour");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await mesApi.getFlourQualityQueue();
      setQueue(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const msg = err.message || "Greška pri učitavanju reda čekanja";
      setLoadError(msg);
      setQueue([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleApprove = useCallback(
    async (lotId: number) => {
      try {
        setApprovingLotId(lotId);
        await mesApi.releaseMaterialLot(lotId, "operator");
        toast.success("Stavka odobrena — puštena iz karantene u zalihu.");
        await loadQueue();
      } catch (err: any) {
        toast.error(`Odobravanje nije uspjelo: ${err.message}`);
      } finally {
        setApprovingLotId(null);
      }
    },
    [loadQueue]
  );

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const flourQueue = queue.filter((row) => row.is_flour);
  const otherQueue = queue.filter((row) => !row.is_flour);
  const rows = activeTab === "flour" ? flourQueue : otherQueue;

  // Selection is per tab: switching tabs would otherwise keep hidden rows
  // selected and silently approve them.
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const switchTab = (tab: QueueTab) => {
    setActiveTab(tab);
    clearSelection();
  };

  const toggleSelect = (lotId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) next.delete(lotId);
      else next.add(lotId);
      return next;
    });
  };

  const allVisibleSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.lot_id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) clearSelection();
    else setSelectedIds(new Set(rows.map((row) => row.lot_id)));
  };

  const handleBulkApprove = async () => {
    const lotIds = rows.map((row) => row.lot_id).filter((id) => selectedIds.has(id));
    if (lotIds.length === 0) return;

    setBulkApproving(true);
    try {
      const results = await Promise.allSettled(
        lotIds.map((lotId) => mesApi.releaseMaterialLot(lotId, "operator"))
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      const approved = results.length - failed;

      if (approved > 0) {
        toast.success(`${approved} stavki odobreno — puštene iz karantene u zalihu.`);
      }
      if (failed > 0) {
        toast.error(`${failed} stavki nije odobreno.`);
      }
      clearSelection();
      await loadQueue();
    } finally {
      setBulkApproving(false);
    }
  };

  const renderSelectAllHeader = () => (
    <th style={{ width: 32 }}>
      <input
        type="checkbox"
        checked={allVisibleSelected}
        onChange={toggleSelectAll}
        title="Odaberi sve"
      />
    </th>
  );

  const renderSelectCell = (row: FlourQualityQueueItem) => (
    <td>
      <input
        type="checkbox"
        checked={selectedIds.has(row.lot_id)}
        onChange={() => toggleSelect(row.lot_id)}
      />
    </td>
  );

  const renderApproveCell = (row: FlourQualityQueueItem) => (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
      <button
        type="button"
        className="btn-save btn-sm"
        disabled={approvingLotId === row.lot_id}
        onClick={() => handleApprove(row.lot_id)}
      >
        {approvingLotId === row.lot_id ? "Odobravanje…" : "Odobri"}
      </button>
      {row.is_flour && (
        <Link to={`/mes/flour-analyses/lot/${row.lot_id}`} className="btn-primary btn-sm">
          Analiza
        </Link>
      )}
    </div>
  );

  return (
    <FlourQualityShell>
      <ToastContainer position="top-center" autoClose={2500} />

      <div className="mes-section">
        <h3 style={{ margin: 0, border: "none", padding: 0 }}>Red čekanja — karantena</h3>
        <p className="iwt-role-hint" style={{ marginTop: 8, marginBottom: 0 }}>
          Artikli u karanteni nakon primke. Kliknite <strong>Odobri</strong> za ručno puštanje stavke u zalihu,
          ili označite više stavki i odobrite ih odjednom.
          Za brašno dodatno otvorite <strong>Analizu</strong> za pregled blend-a, mjerenja i QC odluku.
        </p>
      </div>

      <div className="mes-section">
        <div
          className="fq-subnav"
          role="tablist"
          style={{ marginBottom: 16 }}
        >
          <button
            type="button"
            role="tab"
            className={activeTab === "flour" ? "active" : ""}
            onClick={() => switchTab("flour")}
          >
            Brašno ({flourQueue.length})
          </button>
          <button
            type="button"
            role="tab"
            className={activeTab === "other" ? "active" : ""}
            onClick={() => switchTab("other")}
          >
            Ostali artikli ({otherQueue.length})
          </button>
        </div>

        <div className="wh-pregled-toolbar" style={{ marginBottom: 16 }}>
          <button type="button" className="btn-secondary btn-sm" onClick={loadQueue} disabled={loading}>
            Osvježi
          </button>
        </div>

        {selectedIds.size > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 16,
              padding: "10px 14px",
              borderRadius: 6,
              background: "rgba(37,99,235,0.08)",
              border: "1px solid rgba(37,99,235,0.35)",
            }}
          >
            <strong>{selectedIds.size} odabrano</strong>
            <button
              type="button"
              className="btn-save btn-sm"
              disabled={bulkApproving}
              onClick={handleBulkApprove}
            >
              {bulkApproving ? "Odobravanje…" : `Odobri odabrano (${selectedIds.size})`}
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={bulkApproving}
              onClick={clearSelection}
            >
              Odustani
            </button>
          </div>
        )}

        {loadError && (
          <p className="mes-hint" style={{ color: "#dc3545" }}>
            {loadError} — provjerite je li Hive pokrenut.
          </p>
        )}

        {loading && queue.length === 0 && !loadError ? (
          <p>Učitavanje…</p>
        ) : !loadError && rows.length === 0 ? (
          <p className="mes-hint">
            {activeTab === "flour" ? "Nema brašna u karanteni." : "Nema ostalih artikala u karanteni."}
          </p>
        ) : rows.length > 0 ? (
          activeTab === "flour" ? (
            <div className="mes-table-wrap">
              <table className="mes-table">
              <thead>
                <tr>
                  {renderSelectAllHeader()}
                  <th>LOT</th>
                  <th>Sirovina</th>
                  <th>Dobavljač</th>
                  <th>Primka</th>
                  <th>Datum</th>
                  <th>kg</th>
                  <th>Farinograf</th>
                  <th>Extensograf</th>
                  <th>Amylograf</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.lot_id} className={selectedIds.has(row.lot_id) ? "selected-row" : ""}>
                    {renderSelectCell(row)}
                    <td>
                      <strong>{row.lot_number_supplier || row.internal_lot_code || row.lot_id}</strong>
                    </td>
                    <td>{row.ingredient_name || "—"}</td>
                    <td>{row.supplier_name || "—"}</td>
                    <td>{row.delivery_note_number || "—"}</td>
                    <td>{row.arrival_date ? formatHrDate(row.arrival_date) : "—"}</td>
                    <td className="fq-num">{row.quantity_received_kg?.toFixed(2)}</td>
                    <td>
                      <span className={deviceSummaryBadge(row.measurements?.farinograph).className}>
                        {deviceSummaryBadge(row.measurements?.farinograph).label}
                      </span>
                    </td>
                    <td>
                      <span className={deviceSummaryBadge(row.measurements?.extensograph).className}>
                        {deviceSummaryBadge(row.measurements?.extensograph).label}
                      </span>
                    </td>
                    <td>
                      <span className={deviceSummaryBadge(row.measurements?.amylograph).className}>
                        {deviceSummaryBadge(row.measurements?.amylograph).label}
                      </span>
                    </td>
                    <td>{renderApproveCell(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : (
            <div className="mes-table-wrap">
              <table className="mes-table">
              <thead>
                <tr>
                  {renderSelectAllHeader()}
                  <th>LOT</th>
                  <th>Sirovina</th>
                  <th>Tip</th>
                  <th>Dobavljač</th>
                  <th>Primka</th>
                  <th>Datum</th>
                  <th>kg</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.lot_id} className={selectedIds.has(row.lot_id) ? "selected-row" : ""}>
                    {renderSelectCell(row)}
                    <td>
                      <strong>{row.lot_number_supplier || row.internal_lot_code || row.lot_id}</strong>
                    </td>
                    <td>{row.ingredient_name || "—"}</td>
                    <td>{row.ingredient_type || "—"}</td>
                    <td>{row.supplier_name || "—"}</td>
                    <td>{row.delivery_note_number || "—"}</td>
                    <td>{row.arrival_date ? formatHrDate(row.arrival_date) : "—"}</td>
                    <td className="fq-num">{row.quantity_received_kg?.toFixed(2)}</td>
                    <td>{renderApproveCell(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )
        ) : null}
      </div>
    </FlourQualityShell>
  );
};

export default FlourQualityQueue;
